import { Alert, Button, Drawer, Form, Input, InputNumber } from 'antd';
import { useEffect } from 'react';
import type { PartTypeView } from '../part-types.api';
import { toPartTypeFormValues, type PartTypeFormValues } from '../policies/part-type-form.policy';

interface PartTypeFormDrawerProps {
  open: boolean;
  editing?: PartTypeView;
  error: string | null;
  submitting: boolean;
  onSubmit: (values: PartTypeFormValues) => Promise<boolean>;
  onClose: () => void;
}

/** A short form: a name and, optionally, how long the part should last. A replacement sooner than either figure is early failure. */
export function PartTypeFormDrawer({ open, editing, error, submitting, onSubmit, onClose }: PartTypeFormDrawerProps) {
  const [form] = Form.useForm<PartTypeFormValues>();

  useEffect(() => {
    if (open) form.setFieldsValue(toPartTypeFormValues(editing));
  }, [open, editing, form]);

  async function submit(values: PartTypeFormValues) {
    if (await onSubmit(values)) onClose();
  }

  return (
    <Drawer
      title={editing ? `Edit ${editing.name}` : 'New part type'}
      open={open}
      onClose={onClose}
      destroyOnHidden
      footer={
        <Button type="primary" loading={submitting} onClick={() => form.submit()}>
          {editing ? 'Save changes' : 'Create part type'}
        </Button>
      }
    >
      {error && <Alert type="error" title={error} showIcon className="mb-4" />}
      <Form form={form} layout="vertical" onFinish={submit} initialValues={toPartTypeFormValues(editing)}>
        <Form.Item name="name" label="Name" rules={[{ required: true, whitespace: true, message: 'Enter a name' }]}>
          <Input placeholder="Brake pad" />
        </Form.Item>
        <Form.Item
          name="expectedLifeKm"
          label="Expected life (km)"
          extra="Replaced sooner than this on the same truck counts as early failure. Leave empty to not judge by distance."
        >
          <InputNumber min={1} max={10_000_000} precision={0} className="w-full" />
        </Form.Item>
        <Form.Item name="expectedLifeDays" label="Expected life (days)" extra="Leave empty to not judge by time.">
          <InputNumber min={1} max={36_500} precision={0} className="w-full" />
        </Form.Item>
      </Form>
    </Drawer>
  );
}
